/**
 * Export Button Component
 * 
 * Dropdown button for exporting meeting data in various formats
 */

import {useState, useRef, useEffect} from 'react';
import {Button} from '../ui/Button';
import {Loading} from '../ui/Loading';
import {cn} from '../../utils/cn';
import {getAuth} from 'firebase/auth';
import {app} from '../../config/firebase';

export type ExportFormat = 'pdf' | 'docx' | 'txt';

interface ExportButtonProps {
  meetingId: string;
  meetingTitle?: string;
  className?: string;
}

/**
 * Get API base URL
 */
function getApiBaseUrl(): string {
  // Check if we're using emulator
  const useEmulator = import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'true';
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID || 'voxapilot-prod';
  
  if (useEmulator) {
    return `http://localhost:5001/${projectId}/us-central1`;
  }
  
  // Try to get from env, fallback to default
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
  if (apiBaseUrl) {
    return apiBaseUrl;
  }
  
  // Default production URL
  return `https://us-central1-${projectId}.cloudfunctions.net`;
}

/**
 * Download file from blob
 */
function downloadFile(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/**
 * Export meeting data
 */
async function exportMeeting(
  meetingId: string,
  format: ExportFormat,
  options?: {
    includeTranscript?: boolean;
    includeSummary?: boolean;
    includeSegments?: boolean;
  }
): Promise<void> {
  const auth = getAuth(app);
  const user = auth.currentUser;

  if (!user) {
    throw new Error('User must be authenticated to export meetings');
  }

  // Get auth token
  const token = await user.getIdToken();

  // Build query parameters
  const params = new URLSearchParams({
    format,
  });

  if (options?.includeTranscript !== undefined) {
    params.append('includeTranscript', String(options.includeTranscript));
  }
  if (options?.includeSummary !== undefined) {
    params.append('includeSummary', String(options.includeSummary));
  }
  if (options?.includeSegments !== undefined) {
    params.append('includeSegments', String(options.includeSegments));
  }

  // Get API base URL
  const apiBaseUrl = getApiBaseUrl();
  const url = `${apiBaseUrl}/export/${meetingId}?${params.toString()}`;

  // Fetch export file
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error?.message || `Export failed: ${response.status} ${response.statusText}`
    );
  }

  // Get filename from Content-Disposition header or generate one
  const contentDisposition = response.headers.get('Content-Disposition');
  let filename = `meeting_${meetingId}.${format}`;
  
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename="(.+)"/);
    if (filenameMatch) {
      filename = filenameMatch[1];
    }
  }

  // Get blob and download
  const blob = await response.blob();
  downloadFile(blob, filename);
}

export function ExportButton({meetingId, className}: ExportButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportingFormat, setExportingFormat] = useState<ExportFormat | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  const handleExport = async (format: ExportFormat) => {
    try {
      setIsExporting(true);
      setExportingFormat(format);
      setExportError(null);

      await exportMeeting(meetingId, format);

      // Close dropdown after successful export
      setIsOpen(false);
    } catch (error: any) {
      console.error('Export failed:', error);
      setExportError(error.message || 'Failed to export meeting');
    } finally {
      setIsExporting(false);
      setExportingFormat(null);
    }
  };

  const formatLabels: Record<ExportFormat, string> = {
    pdf: 'PDF Document',
    docx: 'Word Document',
    txt: 'Text File',
  };

  const formatIcons: Record<ExportFormat, string> = {
    pdf: '📄',
    docx: '📝',
    txt: '📋',
  };

  return (
    <div className={cn('relative', className)} ref={dropdownRef}>
      <Button
        variant="outline"
        onClick={() => setIsOpen(!isOpen)}
        disabled={isExporting}
        className="w-full"
      >
        {isExporting ? (
          <>
            <Loading size="sm" className="mr-2" />
            Exporting {exportingFormat?.toUpperCase()}...
          </>
        ) : (
          <>
            <span className="mr-2">📥</span>
            Export Meeting
          </>
        )}
      </Button>

      {isOpen && !isExporting && (
        <div className="absolute top-full left-0 mt-2 w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
          <div className="p-2">
            <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 px-3 py-2 uppercase tracking-wide">
              Export Format
            </div>
            {(['pdf', 'docx', 'txt'] as ExportFormat[]).map((format) => (
              <button
                key={format}
                onClick={() => handleExport(format)}
                className="w-full text-left px-3 py-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
              >
                <span>{formatIcons[format]}</span>
                <span className="text-sm text-gray-900 dark:text-white">
                  {formatLabels[format]}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {exportError && (
        <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-800 dark:text-red-200">
          {exportError}
        </div>
      )}
    </div>
  );
}

